/* eslint-disable @typescript-eslint/no-explicit-any */
import { createRoot, createSignal } from "solid-js";

import { GetPhoneVerifyCode } from "@/api/clientApi/login";
import {
  addNewSecurityEmail,
  addNewSecurityPhone,
  addNewUserPassword,
  changePassword,
  getUserInfoDetails,
  setUserInfos,
  setUserPrivacySetting,
} from "@/api/clientApi/user";
import { message } from "@/components/Base/Message/Message.tsx";
import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog";
import { PHONE_CODE } from "@/constants/constants.ts";
import { CodeTypeEnum, VerifyTypeEnum } from "@/enum/loginRegister";
import { getLangKey, t } from "@/i18n";
import { pubSub } from "@/lib/PubSub";
import { rsaEncrypt } from "@/lib/aesUtils";
import { sdkManager } from "@/lib/sdk/sdkManager";
import { getAppType, getOS } from "@/lib/utils";
import {
  isPasswordMatch,
  isValidCPF,
  isValidPassword,
  isValidPhone,
} from "@/lib/validateUtils.ts";
import { KYCStore } from "@/stores/kycStore.ts";
import { userStore } from "@/stores/userStore.ts";
import { PubSubEvents } from "@/types/PubSub";
import {
  KYCVerifyStatusResult,
  UserKycAuthActionType,
} from "@/types/compontent/base/baseEnum.ts";
import type {
  GetUserInfoDetailsRsp,
  SetUserInfoReq,
  SetUserPrivacySettingReq,
} from "@/types/swagger/front";

/**
 * 待办事项
 *
 * - 确认组件使用 hooks 状况
 * - 清理多馀未使用参数(过去留下的)
 */

type SecurityInfo = {
  email: string;
  phone: string;
  password: boolean;
};

export enum PersonalInfoStatus {
  Empty = 0,
  Partial = 1,
  Complete = 2,
}

type SettingsStrengthType = {
  security: PersonalInfoStatus;
  verify: {
    personal_info: PersonalInfoStatus;
    kyc: KYCVerifyStatusResult;
  };
};

// 创建settings store的工厂函数
function createSettings() {
  const { closeDialog } = useDialog();
  const [userInfo, setUserInfo] = createSignal<GetUserInfoDetailsRsp | null>(
    null,
  );

  const [hideGameData, setHideGameData] = createSignal(false);
  const [hideNickName, setHideNickName] = createSignal(false);
  const [rejectTip, setRejectTip] = createSignal(false);
  const [rejectEmail, setRejectEmail] = createSignal(false);
  const [rejectPhone, setRejectPhone] = createSignal(false);

  // 绑定 contact_info ( acode+phone / email )
  const [bandedEmail, setBandedEmail] = createSignal("");
  const [bandedPhone, setBandedPhone] = createSignal("");
  const [bandedACode, setBandedACode] = createSignal(PHONE_CODE);

  // KYC 使用
  const [CPF, setCPF] = createSignal("");
  const [bandedPhoneError, setBandedPhoneError] = createSignal("");
  const [CPFError, setCPFError] = createSignal("");

  // 修改密码
  const [currentPassword, setCurrentPassword] = createSignal("");
  const [newPassword, setNewPassword] = createSignal("");
  const [confirmPassword, setConfirmPassword] = createSignal("");

  // 修改密码错误提示
  const [currentPasswordError, setCurrentPasswordError] = createSignal("");
  const [newPasswordError, setNewPasswordError] = createSignal("");
  const [confirmPasswordError, setConfirmPasswordError] = createSignal("");

  // 安全设定
  const [securityInfo, setSecurityInfo] = createSignal<SecurityInfo>({
    email: "",
    phone: "",
    password: false,
  });
  // loading
  const [submitLoading, setSubmitLoading] = createSignal(false);

  // 安全设定整体强度   // 0 (weak) | 1 (medium) | 2 (strong) | null
  const [settingsStrength, setSettingsStrength] =
    createSignal<SettingsStrengthType>({
      security: 0,
      verify: {
        personal_info: 0,
        kyc: 0,
      },
    });

  // 获取资料更新置(api)
  const getUserInfo = async () => {
    const res = await getUserInfoDetails();
    setUserInfo(res as GetUserInfoDetailsRsp);
    pubSub.publish(
      PubSubEvents.UserInfoUpdated,
      res?.verify?.personal_info?.contact_email ||
        res?.verify?.personal_info?.contact_phone,
    );

    // privacy - 是否隐藏游戏资讯
    setHideGameData(res?.account_info?.privacy?.hide_game_data ?? false);
    // privacy - 是否隐藏昵称
    setHideNickName(res?.account_info?.privacy?.hide_nick_name ?? false);
    // marketing - 是否拒绝接收营销资讯
    setRejectTip(res?.account_info?.privacy?.reject_tip ?? false);
    // marketing - 是否拒绝接收邮件资讯
    setRejectEmail(res?.account_info?.marketing?.reject_email ?? false);
    // marketing - 是否拒绝接收手机资讯
    setRejectPhone(res?.account_info?.marketing?.reject_phone ?? false);

    // 安全设定 ( acode+phone / email )
    setSecurityInfo({
      email: res?.security?.security_email?.email ?? "",
      phone: res?.security?.security_phone?.phone.split("+")[1] ?? "", // +55 2222222 => 55 2222222
      password: res?.security?.passward ?? false,
    });

    // 处理绑定的 phone / email / acode
    if (res?.account_info?.contact_info?.email !== "#") {
      setBandedEmail(res?.account_info?.contact_info?.email ?? "");
    }
    if (res?.account_info?.contact_info?.phone !== "#") {
      const parts = res?.account_info?.contact_info?.phone?.split("#") || [];
      setBandedACode(parts[0]);
      setBandedPhone(parts[1]);
    }

    // Security Strength
    const security = res?.security;

    const list: string[] = [];
    if (security?.passward) list.push("password");
    if (security?.security_email?.status === 1) list.push("email");
    if (security?.security_phone?.status === 1) list.push("phone");
    if (security?.google_2fa?.status === 1) list.push("google");

    // 没设置安全项: 0 (weak) / 只设置 1~2 项: 1 (medium) / 设置了3 项以上 : 2 (strong)
    const strength = list.length === 0 ? 0 : list.length <= 2 ? 1 : 2;

    /* prettier-ignore */ //Verify Info Strength -> 排除 google_code, google_status, sex(已经预设男)
    const { google_code, google_status, sex, ...tempInfo } = userInfo()?.verify.personal_info ?? {};

    /* prettier-ignore */
    const hasEmptyData = Object.values(tempInfo).some((value) => value === "" || value === undefined || value === null);
    /* prettier-ignore */
    const hasAllEmptyData = Object.values(tempInfo).every(
      (value) => value === "" || value === undefined || value === null
    );

    // Todo 要接 api 返回值
    // Verify KYC Strength
    const kycInfo = res?.verify?.basic_verification?.kyc_info ?? {};
    const kycStatus =
      KYCVerifyStatusResult.success === kycInfo?.kyc_status
        ? 2
        : KYCVerifyStatusResult.pendding === kycInfo?.kyc_status
          ? 1
          : 0;

    // 没资料 0 / 有资料但没填齐 1 / 有资料且填齐了 2
    const verifyStrength = hasAllEmptyData ? 0 : hasEmptyData ? 1 : 2;

    setSettingsStrength({
      security: strength,
      verify: { personal_info: verifyStrength, kyc: kycStatus },
    });
  };

  // 更新: 使用者设置(api) -> privacy & marketing
  const updateUserSetting = async (
    type: keyof SetUserPrivacySettingReq,
    value: boolean,
  ) => {
    const param: Partial<SetUserPrivacySettingReq> = {
      [type]: {
        is_setting: true,
        value,
      },
    };

    const res = await setUserPrivacySetting(param as SetUserPrivacySettingReq);

    switch (type) {
      case "hide_game_data":
        setHideGameData(value);
        break;
      case "hide_nick_name":
        setHideNickName(value);
        break;
      case "reject_tip":
        setRejectTip(value);
        break;
      case "reject_email":
        setRejectEmail(value);
        break;
      case "reject_phone":
        setRejectPhone(value);
        break;
    }

    return res;
  };

  const updateContactInfo = async (
    type: "email" | "phone",
    value: string,
    areaCode?: string,
  ) => {
    try {
      const mockSuccess = true;
      const data = {
        contact_email: "",
        contact_phone: "",
        contact_a_code: "",
      };

      if (mockSuccess) {
        if (type === "email") {
          data.contact_email = await rsaEncrypt(value || "");
          setBandedEmail(value);
        } else if (type === "phone" && areaCode) {
          data.contact_phone = await rsaEncrypt(value || "");
          data.contact_a_code = areaCode;
          setBandedPhone(value);
          setBandedACode(areaCode);
        }
        const res = await setUserInfos(data);
        return { success: true };
      }
      return { success: false, message: t("settings.updatefailed") };
    } catch (error) {
      return { success: false, message: t("settings.erroroccurred") };
    }
  };

  // 验证密码
  const validatePassword = () => {
    if (!isValidPassword(currentPassword())) {
      setCurrentPasswordError(t("settings.invalidpassword"));
      return false;
    } else {
      setCurrentPasswordError("");
    }

    return true;
  };

  // 验证新密码
  const validateNewPassword = () => {
    if (!isValidPassword(newPassword())) {
      setNewPasswordError(t("settings.invalidpassword"));
      return false;
    } else {
      setNewPasswordError("");
    }
    return true;
  };

  // 验证确认密码
  const validateConfirmPassword = () => {
    if (!isPasswordMatch(newPassword(), confirmPassword())) {
      setConfirmPasswordError(t("settings.passwordmismatch"));
      return false;
    } else {
      setConfirmPasswordError("");
    }
    return true;
  };

  // 验证手机号
  const validateBandedPhone = () => {
    if (!bandedPhone()) {
      setBandedPhoneError(t("settings.phonerequired"));
      return false;
    }

    if (bandedPhone().length <= 5) {
      setBandedPhoneError(t("settings.phonelength"));
      return false;
    }

    // // 6~10 位数字：通过
    // if (/^\d{6,10}$/.test(bandedPhone())) {
    //   setBandedPhoneError("");
    //   return true;
    // }

    // 巴西 11位特殊验证 /** 前两位不为0 , 第三位必须为9 , 后8位不能有重复数字 */
    const elevenDigitRegex = /^[1-9][1-9]9(?!([0-9])\1{7})\d{8}$/;

    if (bandedPhone().length === 11) {
      if (!elevenDigitRegex.test(bandedPhone())) {
        setBandedPhoneError(t("settings.invalidphone11"));
        return false;
      } else {
        setBandedPhoneError("");
        return true;
      }
    }

    setBandedPhoneError(t("settings.invalidphone"));
    return true;
  };

  // 验证CPF
  const validateCPF = () => {
    if (!CPF() || CPF() === "") {
      setCPFError(t("auth.cpfrequired"));
      return false;
    } else if (!isValidCPF(CPF())) {
      setCPFError(t("auth.cpfinvalid"));
      return false;
    } else {
      setCPFError("");
    }

    return true;
  };
  // 更新密码(api)
  const updatePassword = async () => {
    const { default: SparkMD5 } = await import("@repo/sparkmd5");
    if (userInfo()?.security.passward) {
      if (
        !validatePassword() ||
        !validateNewPassword() ||
        !validateConfirmPassword()
      ) {
        return;
      }
    } else {
      if (!validateNewPassword()) {
        return;
      }
    }
    setSubmitLoading(true);
    // 已有密码
    if (userInfo()?.security.passward) {
      // const res = await changePassword({
      //   pwd: SparkMD5.hash(currentPassword() || ""),
      //   new_pwd: SparkMD5.hash(newPassword() || ""),
      //   action_id: "",
      // });
      // if (!res._errno) {
      //   message.show(t("tips.password_updateed"));
      //   closeDialog(DialogType.ChangePwd);
      //   setCurrentPassword("");
      //   setNewPassword("");
      //   setConfirmPassword("");
      //   await getUserInfo();
      // }

      await KYCStore.getUserKycAuthByAction(
        UserKycAuthActionType.resetpwd,
        () =>
          changePassword({
            pwd: SparkMD5.hash(currentPassword() || ""),
            new_pwd: SparkMD5.hash(newPassword() || ""),
            action_id: "",
          })
            .then(async (res): Promise<any> => {
              if (res?.user_auth) return res;
              if (!res?._errno) {
                message.show(t("tips.password_updateed"));
                closeDialog(DialogType.ChangePwd);
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
                await getUserInfo();
              }
            })
            .finally(() => {
              setSubmitLoading(false);
            }),
      );
    } else {
      // 没有密码
      // const res = await addNewUserPassword({
      //   pwd: SparkMD5.hash(newPassword() || ""),
      // });
      // if (!res?._errno) {
      //   message.show(t("tips.password_updateed"));
      //   closeDialog(DialogType.ChangePwd);
      //   setCurrentPassword("");
      //   setNewPassword("");
      //   setConfirmPassword("");
      //   await getUserInfo();
      // }

      await KYCStore.getUserKycAuthByAction(
        UserKycAuthActionType.resetpwd,
        () =>
          addNewUserPassword({
            pwd: SparkMD5.hash(newPassword() || ""),
          })
            .then(async (res): Promise<any> => {
              if (res?.user_auth) return res;
              if (!res?._errno) {
                message.show(t("tips.password_updateed"));
                closeDialog(DialogType.ChangePwd);
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
                await getUserInfo();
              }
            })
            .finally(() => {
              setSubmitLoading(false);
            }),
      );
    }
  };

  // 新增 / 更新 Email (api)
  const updateSecurityEmail = async ({
    email,
    code,
  }: {
    email: string;
    code: string;
  }) => {
    const rsaEmail = await rsaEncrypt(email);

    const res = await addNewSecurityEmail({
      email: rsaEmail,
      code: code,
      action_id: "",
    });
    getUserInfo();
    return res;
  };

  // 新增 / 更新 Phone (api)
  const updateSecurityPhone = async ({
    phone,
    aCode,
    code,
  }: {
    phone: string;
    aCode: string;
    code: string;
  }) => {
    // const res = await addNewUserEmail(email);
    const rsaPhone = await rsaEncrypt(phone);
    const res = await addNewSecurityPhone({
      phone: rsaPhone,
      acode: aCode,
      code: code,
      action_id: "",
    });
    getUserInfo();
    return res;
  };

  // 获取 phone / email验证码 (api)
  const getVerifyCode = async ({
    content,
    type,
    isRetry,
    isBound,
  }: {
    content: string;
    type: "email" | "phone";
    isRetry: boolean;
    isBound?: boolean;
  }) => {
    if (type === "email") {
      const rsaEmail = await rsaEncrypt(content);
      await GetPhoneVerifyCode(
        {
          code_type: isBound // 已经绑定过的传 4 ，未绑定过的传 1
            ? CodeTypeEnum.CodeTypeBind
            : CodeTypeEnum.CodeTypeRegister,
          email: rsaEmail,
          lang: getLangKey(),
          noToken: false,
          retry: isRetry ? 1 : 0,
          verify_type: VerifyTypeEnum.VerifyTypeEmail,
        },
        true,
      );
    }
    if (type === "phone") {
      const [acode, phone] = content.split("-");
      const rsaPhone = await rsaEncrypt(phone);
      await GetPhoneVerifyCode(
        {
          code_type: isBound
            ? CodeTypeEnum.CodeTypeBind
            : CodeTypeEnum.CodeTypeRegister,
          acode,
          phone: rsaPhone,
          lang: getLangKey(),
          noToken: false,
          retry: isRetry ? 1 : 0,
          verify_type: VerifyTypeEnum.VerifyTypePhone,
        },
        true,
      );
    }
  };

  const submitKYCInfo = async (callback?: (result: boolean) => void) => {
    // if (
    //   !validateCPF() ||
    //   (!userStore?.userInfo?.phone && !validateBandedPhone()) ||
    //   (userStore?.userInfo?.is_set_password !== 1 && !validateNewPassword())
    // ) {
    //   return;
    // }
    if (!validateCPF()) return;
    // const { default: SparkMD5 } = await import("@repo/sparkmd5");
    // const md5Pwd = SparkMD5.hash(newPassword() || "");
    // const phone = await rsaEncrypt(bandedPhone() || "");
    const params = {
      // phone: phone,
      // password: md5Pwd,
      cpf: CPF(),
    } as SetUserInfoReq;
    // if (!userStore?.userInfo?.phone) params.phone = phone;
    // if (userStore?.userInfo?.is_set_password !== 1) params.password = md5Pwd;
    await setUserInfos(params)
      .then((res) => {
        if (res) {
          // message.show(t("common.success"));
          callback?.(true);
        } else callback?.(false);
      })
      .catch(() => callback?.(false));
  };

  return {
    userInfo,

    // privacy & marketing
    hideGameData,
    hideNickName,
    rejectTip,
    rejectEmail,
    rejectPhone,
    setHideGameData,
    setHideNickName,
    setRejectTip,
    setRejectEmail,
    setRejectPhone,
    submitLoading,
    setSubmitLoading,

    // contact_info (phone / email / acode)
    getUserInfo,
    updateUserSetting,
    bandedEmail,
    bandedPhone,
    bandedACode,
    setBandedEmail,
    setBandedPhone,
    setBandedACode,
    updateContactInfo,

    // KYC 补充信息提交
    CPF,
    setCPF,
    CPFError,
    validateCPF,
    bandedPhoneError,
    validateBandedPhone,
    validateNewPassword,
    submitKYCInfo,

    // 现有密码 / 新密码 / 确认密码 / 更新密码(api)
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    updatePassword,

    // 密码错误讯息
    currentPasswordError,
    setCurrentPasswordError,
    newPasswordError,
    setNewPasswordError,
    confirmPasswordError,
    setConfirmPasswordError,

    // 安全设定整体强度
    settingsStrength,
    // 安全设定 ( acode+phone / email )
    securityInfo,
    // 获取 phone / email验证码
    getVerifyCode,
    // 新增 Email(api)
    updateSecurityEmail,
    // 新增 Phone(api)
    updateSecurityPhone,
  };
}

// 使用createRoot创建单例实例
export const settingsContext = createRoot(() => createSettings());

// 导出hooks函数，返回单例
export const useSettings = () => settingsContext;
